import { Dispatch, SetStateAction } from 'react'

import { useGuardToursTable } from '../hooks/useGuardToursTable'

import { Box } from '@chakra-ui/react'

import {
  TABLE_HEIGHT,
  TABLE_MAX_WIDTH,
  Table,
  TableBody,
  TablePagination,
  TableRow,
} from '@hivewatch/dark'

import { GuardToursListType, GuardToursRowDataType } from '../types/table'
import { GuardToursTableHeader } from './GuardToursTableHeader'
import { GuardToursTableRow } from './GuardToursTableRow'
import { GuardToursTableToolbar } from './GuardToursTableToolbar'

const ROW_HEIGHT = 56
const HEADER_HEIGHT = 40
const PAGINATION_HEIGHT = 56

interface GuardToursTableIProps {
  tours?: GuardToursListType
  totalTours?: number
  filter?: string
  setFilter: (value: string) => void
  onRowClick: (tour: GuardToursRowDataType) => void
  pageIndex: number
  setPageIndex: Dispatch<SetStateAction<number>>
  pageSize: number
  setPageSize: Dispatch<SetStateAction<number>>
  sortColumn: string | null
  setSortColumn: Dispatch<SetStateAction<string | null>>
  sortOrder: 'asc' | 'desc' | null
  setSortOrder: Dispatch<SetStateAction<'asc' | 'desc' | null>>
  isLoading?: boolean
}

export const GuardToursTable = ({
  tours,
  totalTours,
  filter,
  setFilter,
  onRowClick,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  sortColumn,
  setSortColumn,
  sortOrder,
  setSortOrder,
  isLoading,
}: GuardToursTableIProps) => {
  const {
    colorScheme,
    columns,
    tableData,
    pageCount,
    sortableColumns,
    handleSortChange,
    handlePageSizeChange,
  } = useGuardToursTable({
    tours,
    totalTours,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    sortColumn,
    setSortColumn,
    sortOrder,
    setSortOrder,
  })

  const tableHeight = `min(${TABLE_HEIGHT}, ${
    HEADER_HEIGHT +
    (tableData.length || (isLoading ? pageSize : 1)) * ROW_HEIGHT +
    PAGINATION_HEIGHT
  }px)`

  return (
    <>
      <GuardToursTableToolbar
        colorScheme={colorScheme}
        filter={filter ?? ''}
        setFilter={setFilter}
      />
      <Table
        colorScheme={colorScheme}
        height={tableHeight}
        maxWidth={TABLE_MAX_WIDTH}
        onSortChange={handleSortChange}
        sortableColumns={sortableColumns}
      >
        <GuardToursTableHeader columns={columns} />
        <TableBody isLoading={isLoading} skeletonRowCount={pageSize}>
          {!isLoading && tableData.length === 0 ? (
            <TableRow>
              <Box
                alignItems='center'
                display='flex'
                flex='1'
                height='56px'
                justifyContent='center'
              >
                <Box color='text.secondary' fontSize='14px'>
                  No guard tours found
                </Box>
              </Box>
            </TableRow>
          ) : null}
          {tableData.map((row) => (
            <GuardToursTableRow
              columns={columns}
              key={row.id}
              onClick={onRowClick}
              row={row}
            />
          ))}
        </TableBody>
        <TablePagination
          onPageChange={setPageIndex}
          onPageSizeChange={handlePageSizeChange}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeLabel='Tours per page:'
          total={totalTours ?? 0}
        />
      </Table>
    </>
  )
}
